// Hook to get the user's account type with Stripe subscription support
import { useState, useEffect } from "react";
import { useAuth } from './useAuth';
import { 
  getEffectiveAccountType, 
  isSubscriptionActive, 
  isSubscriptionInGracePeriod,
  formatSubscriptionDetails 
} from '../services/stripeMigration';

export const ACCOUNT_TYPES = {
  NEW_AGENT: "New Agent",
  AGENT: "Agent",
  LEADER: "Leader",
  BROKER: "Broker",
  SENIOR_BROKER: "Senior Broker",
  ADMIN: "Admin",
};

export const AGENT_LEVELS = {
    TA: 'TA',          // Training Associate
    A: 'A',           // Associate
    MD: 'MD',          // Marketing Director
    SMD: 'SMD'          // Senior Marketing Director
};

const PAID_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.ADMIN,
  ACCOUNT_TYPES.BROKER,
  ACCOUNT_TYPES.SENIOR_BROKER,
  ACCOUNT_TYPES.LEADER,
  ACCOUNT_TYPES.AGENT
];

export const useAccountType = () => {
  const { user, profile, claims, loading: authLoading } = useAuth();
  const [plan, setAccountType] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [adminViewMode, setAdminViewMode] = useState(
    localStorage.getItem("adminViewMode") || "Admin"
  );

  // Listen for admin view mode changes
  useEffect(() => {
    const handleViewModeChange = (event) => {
      setAdminViewMode(event.detail);
    };
    window.addEventListener("viewModeChanged", handleViewModeChange);
    return () => window.removeEventListener("viewModeChanged", handleViewModeChange);
  }, []);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setAccountType(ACCOUNT_TYPES.NEW_AGENT);
      setIsPaid(false);
      setLoading(false);
      return;
    }

    // Determine if user is admin
    const isAdminUser = !!(
      (claims && (claims.role === "Admin" || claims.role === "admin" || claims.admin === true)) ||
      (profile &&
        (String(profile.role || "") === "Admin" ||
          String(profile.role || "").toLowerCase() === "admin" ||
          profile.isAdmin === true ||
          String(profile.plan || "") === "Admin" ||
          String(profile.plan || "").toLowerCase() === "admin"))
    );

    // If admin, use the admin view mode
    if (isAdminUser) {
      setAccountType(adminViewMode);
      setIsPaid(PAID_ACCOUNT_TYPES.includes(adminViewMode));
      setSubscriptionDetails(null); // Admin preview mode
      setLoading(false);
      return;
    }

    // Get account type from Stripe subscription status
    let currentAccountType = ACCOUNT_TYPES.NEW_AGENT;
    
    if (profile) {
      // Check if user has Stripe subscription
      if (profile.stripeSubscriptionId) {
        const status = profile.subscriptionStatus;
        
        // Active or in grace period - use their subscription-based account type
        if (isSubscriptionActive(status) || isSubscriptionInGracePeriod(status)) {
          currentAccountType = getEffectiveAccountType(profile);
        } else {
          // Inactive subscription - downgrade to New Agent
          currentAccountType = ACCOUNT_TYPES.NEW_AGENT;
        }
        
        // Set subscription details for display
        setSubscriptionDetails(formatSubscriptionDetails(profile));
      } else {
        // No Stripe subscription - use profile data
        // Check profile first (more up-to-date than claims)
        if (profile.plan) {
          currentAccountType = profile.plan;
        } else if (claims?.plan) {
          currentAccountType = claims.plan;
        }
      }
    }

    setAccountType(currentAccountType);
    setIsPaid(PAID_ACCOUNT_TYPES.includes(currentAccountType));
    setLoading(false);

  }, [user, profile, claims, authLoading, adminViewMode]);

  // Helper flags
  const isFree = plan === ACCOUNT_TYPES.NEW_AGENT;
  const isAdmin = plan === ACCOUNT_TYPES.ADMIN;
  const isBroker = plan === ACCOUNT_TYPES.BROKER;
  const isPremium = plan === ACCOUNT_TYPES.SENIOR_BROKER;
  const hasActiveSubscription = subscriptionDetails?.hasSubscription && 
                                 subscriptionDetails?.statusColor === 'green';

  return {
    plan,
    isPaid,
    isFree,
    isAdmin,
    isBroker,
    isPremium,
    loading,
    adminViewMode,
    // Stripe subscription details
    subscriptionDetails,
    hasActiveSubscription,
    subscriptionStatus: profile?.subscriptionStatus,
    stripeCustomerId: profile?.stripeCustomerId,
  };
};

